import { TogetherDataTransforms } from '@hhgtech/hhg-components'
import { NavMenuContentType } from '@hhgtech/hhg-components/components/navigationV2/index'
import { GetServerSideProps } from 'next'

import { callApiNew } from 'api/axios'
import { formatExpertUser } from 'api/dataTransform'
import { replaceUndefinedWithNull } from 'api/dataTransform/utils'
import { PATHS } from 'api/paths'
import { cleanUnicodeUrl } from 'api/utils/paths'
import { checkMobile } from 'api/utils/validators'
import { ExpertTemplate } from 'components/pages/expert/withExpertTemplate'
import { ExpertUser, FooterInfo, TogetherPost } from 'interfaces/types'
import { AsyncReturnType } from 'interfaces/types/Utilities'
import { ExpertStoreProvider, fetchRelatedPosts } from 'state/expert'
import { getFooterDataPersist } from 'utils/isomorphicCache/footerData'
import { getNavMenuContentPersist } from 'utils/isomorphicCache/navMenuContent'

export type Props = {
  expert: ExpertUser
  isMobileSsr: boolean
  footerInfo: FooterInfo
  dataNavMenuContent?: NavMenuContentType
  currentExpertRelativePosts: AsyncReturnType<typeof fetchRelatedPosts>
  currentExpertMedicalReviewed: AsyncReturnType<typeof fetchRelatedPosts>
  relatedCardPosts: TogetherPost[]
  relatedCardPostComments: TogetherPost[]
}

const ExpertPage = ({
  expert,
  isMobileSsr,
  footerInfo,
  dataNavMenuContent,
  currentExpertRelativePosts,
  currentExpertMedicalReviewed,
  relatedCardPosts,
  relatedCardPostComments,
}: Props) => {
  return (
    <ExpertStoreProvider
      init={{
        currentExpert: expert,
        currentExpertRelativePosts: currentExpertRelativePosts?.articles || [],
        currentExpertMedicalReviewed:
          currentExpertMedicalReviewed?.articles || [],
        currentExpertMedicalReviewedPagination:
          currentExpertMedicalReviewed?.pagination,
        currentExpertRelativePostsPagination:
          currentExpertRelativePosts?.pagination,
        relatedCardPosts,
        relatedCardPostComments,
      }}
      refreshKey={String(expert.id || '')}
    >
      <ExpertTemplate
        expert={expert}
        isMobileSsr={isMobileSsr}
        footerInfo={footerInfo}
        dataNavMenuContent={dataNavMenuContent}
      />
    </ExpertStoreProvider>
  )
}

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const { query, locale, req } = ctx
  const { slug } = query

  const finalSlug = Array.isArray(slug) ? slug.join('/') : slug

  const params = locale === 'en-PH' ? { lang: 'en' } : {}
  const [expertRes, footerInfo, dataNavMenuContent] = await Promise.all([
    callApiNew(PATHS.COMMON.GET_AUTHOR, {
      params: {
        slug: finalSlug || '',
        ...params,
      },
    }),
    getFooterDataPersist(),
    getNavMenuContentPersist(locale),
  ])

  if (expertRes.status === 2 && expertRes.data?.redirect_url) {
    return {
      redirect: {
        destination: cleanUnicodeUrl(expertRes.data.redirect_url),
        statusCode: 301,
      },
    }
  }

  if (
    !expertRes ||
    expertRes.status !== 1 ||
    expertRes?.data?.turn_off_profile ||
    !expertRes?.data?.role
  ) {
    return {
      notFound: true,
    }
  }

  if (!['expert', 'medical'].includes(expertRes?.data?.role || '')) {
    return {
      redirect: {
        destination: cleanUnicodeUrl(`/author/${finalSlug}`),
        statusCode: 301,
      },
    }
  }

  if (
    expertRes?.data?.slug &&
    // forcibly format both side so that it will be the same when compare
    cleanUnicodeUrl(finalSlug || '') !== cleanUnicodeUrl(expertRes?.data?.slug)
  ) {
    return {
      redirect: {
        destination: cleanUnicodeUrl(`/expert/${expertRes?.data?.slug}`),
        statusCode: 301,
      },
    }
  }

  const [
    currentExpertRelativePosts,
    currentExpertMedicalReviewed,
    relatedPostsRes,
    relatedPostCommentRes,
  ] = await Promise.all([
    fetchRelatedPosts({
      id: expertRes.data.user_id,
      type: 0,
      numberOfPost: 7,
    }),
    fetchRelatedPosts({
      id: expertRes.data.user_id,
      type: 1,
      numberOfPost: 7,
    }),
    callApiNew(
      PATHS.TOGETHER.USER.LIST_POST,
      {
        params: {
          hhgExpertId: expertRes.data.user_id,
          isAnonymous: 0,
          pageSize: 10,
        },
      },
      'together',
    ),
    callApiNew(
      PATHS.TOGETHER.USER.LIST_ANSWERS,
      {
        params: {
          id: expertRes.data.user_id,
          filterByExpertHhd: 1,
          pageSize: 10,
        },
      },
      'together',
    ),
  ])
  const userAgent = `${req.headers['user-agent']}`

  const isMobileSsr = checkMobile(userAgent)

  return {
    props: {
      isMobileSsr,
      expert: formatExpertUser(expertRes.data),
      footerInfo: footerInfo?.data || null,
      dataNavMenuContent: dataNavMenuContent || {},
      currentExpertRelativePosts: replaceUndefinedWithNull(
        currentExpertRelativePosts,
      ),
      currentExpertMedicalReviewed: replaceUndefinedWithNull(
        currentExpertMedicalReviewed,
      ),
      relatedCardPosts: (relatedPostsRes?.data?.posts || []).map(
        TogetherDataTransforms.formatPost,
      ),
      relatedCardPostComments: (relatedPostCommentRes?.data?.replies || []).map(
        TogetherDataTransforms.formatPost,
      ),
    },
  }
}

export default ExpertPage
